import {
  useRef,
  useState,
  useCallback,
  useEffect,
  RefObject,
  PointerEvent as ReactPointerEvent,
  MouseEvent as ReactMouseEvent,
  CSSProperties,
  RefCallback,
} from 'react';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UseCarouselOptions {
  /**
   * Space between items (px). Used when computing per-item scroll steps.
   * @default 0
   */
  spaceBetweenItems?: number;

  /**
   * Enable pointer/mouse drag-to-scroll.
   * @default true
   */
  mouseDragging?: boolean;

  /**
   * Pointer types that can initiate drag-to-scroll.
   * @default ['mouse']
   */
  dragPointerTypes?: Array<'mouse' | 'touch' | 'pen'>;

  /**
   * Pointer delta (px) before a gesture is considered a drag.
   * @default 4
   */
  dragThreshold?: number;

  /**
   * Size token forwarded to arrow button components.
   * @default 'md'
   */
  arrowSize?: 'sm' | 'md' | 'lg';

  /**
   * Visual variant forwarded to arrow button components.
   * @default 'neutral'
   */
  arrowStyleVariant?:
    | 'neutral'
    | 'white'
    | 'shapeFlat'
    | 'shapeElevated'
    | 'gradient';

  /**
   * Focus ring style forwarded to arrow button components.
   * @default 'default'
   */
  focusStyle?: 'default' | 'white';

  /**
   * Only show arrows when the carousel is hovered or focused.
   * @default false
   */
  showArrowsOnHover?: boolean;

  /**
   * Completely hide an arrow when it is disabled (not just grey it out).
   * @default false
   */
  hideDisabledArrows?: boolean;
}

export interface CarouselArrowProps {
  disabled: boolean;
  /** True when hideDisabledArrows is set and the button is disabled. */
  hidden: boolean;
  size: 'sm' | 'md' | 'lg';
  styleVariant: NonNullable<UseCarouselOptions['arrowStyleVariant']>;
  focusStyle: NonNullable<UseCarouselOptions['focusStyle']>;
}

export interface CarouselDragProps {
  onPointerDown?: (e: ReactPointerEvent) => void;
  onPointerMove?: (e: ReactPointerEvent) => void;
  onPointerUp?: (e: ReactPointerEvent) => void;
  onPointerLeave?: (e: ReactPointerEvent) => void;
  onClick?: (e: ReactMouseEvent) => void;
  style?: CSSProperties;
}

export interface UseCarouselReturn {
  /** Attach to the scrollable track element. */
  scrollerRef: RefObject<HTMLElement>;

  /** Callback ref that keeps listeners in sync if the DOM node changes. */
  setScrollerRef: RefCallback<HTMLElement>;

  /** Whether the track can scroll further left. */
  canScrollLeft: boolean;

  /** Whether the track can scroll further right. */
  canScrollRight: boolean;

  /** Derived: canScrollLeft || canScrollRight. */
  isOverflowing: boolean;

  /** Scroll left by one logical page. */
  scrollLeft: () => void;

  /** Scroll right by one logical page. */
  scrollRight: () => void;

  /** Whether the item at the given index is fully visible in the viewport. */
  isItemFullyVisible: (index: number, tolerance?: number) => boolean;

  /** Scroll the item at the given index into view when it exists. */
  scrollToItem: (index: number, behavior?: ScrollBehavior) => void;

  /** Resolved props for the previous arrow button. */
  prevArrowProps: CarouselArrowProps;

  /** Resolved props for the next arrow button. */
  nextArrowProps: CarouselArrowProps;

  /**
   * True when arrows should be visible.
   * Always true when showArrowsOnHover is false; otherwise tracks hover/focus.
   */
  arrowsVisible: boolean;

  /**
   * Spread onto the scroll track to enable mouse drag behaviour.
   * Empty object when mouseDragging is false.
   */
  dragProps: CarouselDragProps;
}

// ─── Constants ────────────────────────────────────────────────────────────────

/** px used as epsilon near scroll boundaries to avoid flicker. */
const SCROLL_THRESHOLD = 2;

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useCarousel(
  options: UseCarouselOptions = {},
): UseCarouselReturn {
  const {
    spaceBetweenItems = 0,
    mouseDragging = true,
    dragPointerTypes = ['mouse'],
    dragThreshold = 4,
    arrowSize = 'md',
    arrowStyleVariant = 'neutral',
    focusStyle = 'default',
    showArrowsOnHover = false,
    hideDisabledArrows = false,
  } = options;

  const scrollerRef = useRef<HTMLElement | null>(null);
  const [scrollerElement, setScrollerElement] = useState<HTMLElement | null>(
    null,
  );
  const rafIdRef = useRef<number | null>(null);

  const setScrollerRef = useCallback<RefCallback<HTMLElement>>((node) => {
    scrollerRef.current = node;
    setScrollerElement(node);
  }, []);

  // ── Overflow state ────────────────────────────────────────────────────────
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // ── Hover/focus for arrow visibility ─────────────────────────────────────
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  // ── Drag state ────────────────────────────────────────────────────────────
  const isDraggingRef = useRef(false);
  const dragStartXRef = useRef(0);
  const dragScrollLeftRef = useRef(0);
  const didDragRef = useRef(false);

  // ── Overflow measurement ──────────────────────────────────────────────────

  const measureOverflow = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;

    const maxScrollLeft = Math.max(0, el.scrollWidth - el.clientWidth);
    const scrollLeft = el.scrollLeft;

    let nextCanScrollLeft = scrollLeft > SCROLL_THRESHOLD;
    let nextCanScrollRight = scrollLeft < maxScrollLeft - SCROLL_THRESHOLD;

    const children = Array.from(el.children).filter(
      (child): child is HTMLElement => child instanceof HTMLElement,
    );
    if (children.length > 1) {
      const viewport = el.parentElement ?? el;
      const viewportRect = viewport.getBoundingClientRect();
      const scrollerRect = el.getBoundingClientRect();
      const visibleLeft = Math.max(viewportRect.left, scrollerRect.left);
      const visibleRight = Math.min(viewportRect.right, scrollerRect.right);

      let leftMost = Number.POSITIVE_INFINITY;
      let rightMost = Number.NEGATIVE_INFINITY;
      for (const child of children) {
        const rect = child.getBoundingClientRect();
        if (rect.left < leftMost) leftMost = rect.left;
        if (rect.right > rightMost) rightMost = rect.right;
      }

      nextCanScrollLeft ||= leftMost < visibleLeft - SCROLL_THRESHOLD;
      nextCanScrollRight ||= rightMost > visibleRight + SCROLL_THRESHOLD;
    }

    setCanScrollLeft((prev) =>
      prev === nextCanScrollLeft ? prev : nextCanScrollLeft,
    );
    setCanScrollRight((prev) =>
      prev === nextCanScrollRight ? prev : nextCanScrollRight,
    );
  }, []);

  const scheduleMeasure = useCallback(() => {
    if (rafIdRef.current !== null) return;
    rafIdRef.current = window.requestAnimationFrame(() => {
      rafIdRef.current = null;
      measureOverflow();
    });
  }, [measureOverflow]);

  // ── Setup observers and listeners ─────────────────────────────────────────

  useEffect(() => {
    const el = scrollerElement;
    if (!el || typeof window === 'undefined') return;

    // Initial measurement
    scheduleMeasure();

    const canUseResizeObserver = typeof ResizeObserver !== 'undefined';
    const canUseMutationObserver = typeof MutationObserver !== 'undefined';

    let ro: ResizeObserver | null = null;
    let mo: MutationObserver | null = null;

    // ResizeObserver watches the scroller and its parent
    if (canUseResizeObserver) {
      ro = new ResizeObserver(scheduleMeasure);
      ro.observe(el);
      if (el.parentElement) ro.observe(el.parentElement);
    } else {
      // Fallback for environments without ResizeObserver support.
      window.addEventListener('resize', scheduleMeasure, { passive: true });
    }

    // MutationObserver watches childList/subtree DOM mutations.
    if (canUseMutationObserver) {
      mo = new MutationObserver(scheduleMeasure);
      mo.observe(el, { childList: true, subtree: true });
    }

    el.addEventListener('scroll', scheduleMeasure, { passive: true });

    return () => {
      if (rafIdRef.current !== null) {
        window.cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      ro?.disconnect();
      mo?.disconnect();
      if (!canUseResizeObserver) {
        window.removeEventListener('resize', scheduleMeasure);
      }
      el.removeEventListener('scroll', scheduleMeasure);
    };
  }, [scheduleMeasure, scrollerElement]);

  // ── Hover/focus tracking for arrowsVisible ────────────────────────────────

  useEffect(() => {
    if (!showArrowsOnHover) return;

    const el = scrollerElement;
    if (!el) return;

    const container = el.parentElement ?? el;

    const onMouseEnter = () => setIsHovered(true);
    const onMouseLeave = () => setIsHovered(false);
    const onFocusIn = () => setIsFocused(true);
    const onFocusOut = (e: FocusEvent) => {
      if (!container.contains(e.relatedTarget as Node | null)) {
        setIsFocused(false);
      }
    };

    container.addEventListener('mouseenter', onMouseEnter);
    container.addEventListener('mouseleave', onMouseLeave);
    container.addEventListener('focusin', onFocusIn);
    container.addEventListener('focusout', onFocusOut);

    return () => {
      container.removeEventListener('mouseenter', onMouseEnter);
      container.removeEventListener('mouseleave', onMouseLeave);
      container.removeEventListener('focusin', onFocusIn);
      container.removeEventListener('focusout', onFocusOut);
    };
  }, [showArrowsOnHover, scrollerElement]);

  // ── Scroll actions ────────────────────────────────────────────────────────

  const scrollLeft = useCallback(() => {
    const el = scrollerRef.current;
    if (!el || !canScrollLeft) return;

    // Include configured item gap so each step better aligns with spaced layouts.
    const normalizedGap =
      Number.isFinite(spaceBetweenItems) && spaceBetweenItems > 0
        ? spaceBetweenItems
        : 0;
    const scrollAmount = el.clientWidth * 0.8 + normalizedGap;

    el.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
  }, [canScrollLeft, spaceBetweenItems]);

  const scrollRight = useCallback(() => {
    const el = scrollerRef.current;
    if (!el || !canScrollRight) return;

    // Include configured item gap so each step better aligns with spaced layouts.
    const normalizedGap =
      Number.isFinite(spaceBetweenItems) && spaceBetweenItems > 0
        ? spaceBetweenItems
        : 0;
    const scrollAmount = el.clientWidth * 0.8 + normalizedGap;

    el.scrollBy({ left: scrollAmount, behavior: 'smooth' });
  }, [canScrollRight, spaceBetweenItems]);

  const isItemFullyVisible = useCallback(
    (index: number, tolerance = 1): boolean => {
      const el = scrollerRef.current;
      if (!el) return true;

      const viewport = el.parentElement ?? el;
      const itemElements = Array.from(el.children).filter(
        (child): child is HTMLElement => child instanceof HTMLElement,
      );
      const target = itemElements[index];

      if (!target) return true;

      const viewportRect = viewport.getBoundingClientRect();
      const scrollerRect = el.getBoundingClientRect();
      const itemRect = target.getBoundingClientRect();

      const visibleLeft = Math.max(viewportRect.left, scrollerRect.left);
      const visibleRight = Math.min(viewportRect.right, scrollerRect.right);

      return (
        itemRect.left >= visibleLeft - tolerance &&
        itemRect.right <= visibleRight + tolerance
      );
    },
    [],
  );

  const scrollToItem = useCallback(
    (index: number, behavior: ScrollBehavior = 'smooth') => {
      const el = scrollerRef.current;
      if (!el) return;

      const itemElements = Array.from(el.children).filter(
        (child): child is HTMLElement => child instanceof HTMLElement,
      );
      const target = itemElements[index];

      if (!target) return;

      target.scrollIntoView({
        block: 'nearest',
        inline: 'nearest',
        behavior,
      });
    },
    [],
  );

  // ── Drag handlers ─────────────────────────────────────────────────────────

  const dragProps: CarouselDragProps = mouseDragging
    ? {
        onPointerDown: (e: ReactPointerEvent) => {
          if (
            !dragPointerTypes.includes(
              e.pointerType as 'mouse' | 'touch' | 'pen',
            )
          ) {
            return;
          }
          const el = scrollerRef.current;
          if (!el) return;
          isDraggingRef.current = true;
          didDragRef.current = false;
          dragStartXRef.current = e.clientX;
          dragScrollLeftRef.current = el.scrollLeft;
          (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
        },
        onPointerMove: (e: ReactPointerEvent) => {
          if (!isDraggingRef.current) return;
          const el = scrollerRef.current;
          if (!el) return;
          const delta = e.clientX - dragStartXRef.current;
          if (Math.abs(delta) > dragThreshold) {
            didDragRef.current = true;
          }
          el.scrollLeft = dragScrollLeftRef.current - delta;
        },
        onPointerUp: (e: ReactPointerEvent) => {
          if (!isDraggingRef.current) return;
          isDraggingRef.current = false;
          (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
          scheduleMeasure();
        },
        onPointerLeave: (e: ReactPointerEvent) => {
          if (!isDraggingRef.current) return;
          isDraggingRef.current = false;
          (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
          scheduleMeasure();
        },
        onClick: (e: ReactMouseEvent) => {
          // Suppress the click that fires immediately after a drag gesture
          if (didDragRef.current) {
            e.preventDefault();
            e.stopPropagation();
            didDragRef.current = false;
          }
        },
        style: { cursor: 'grab', userSelect: 'none' },
      }
    : {};

  // ── Derived values ────────────────────────────────────────────────────────

  const arrowsVisible = showArrowsOnHover ? isHovered || isFocused : true;

  const prevArrowProps: CarouselArrowProps = {
    disabled: !canScrollLeft,
    hidden: hideDisabledArrows && !canScrollLeft,
    size: arrowSize,
    styleVariant: arrowStyleVariant,
    focusStyle,
  };

  const nextArrowProps: CarouselArrowProps = {
    disabled: !canScrollRight,
    hidden: hideDisabledArrows && !canScrollRight,
    size: arrowSize,
    styleVariant: arrowStyleVariant,
    focusStyle,
  };

  return {
    scrollerRef,
    setScrollerRef,
    canScrollLeft,
    canScrollRight,
    isOverflowing: canScrollLeft || canScrollRight,
    scrollLeft,
    scrollRight,
    isItemFullyVisible,
    scrollToItem,
    prevArrowProps,
    nextArrowProps,
    arrowsVisible,
    dragProps,
  };
}
